/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

(function () {
  var hideTimeoutId = null;

  function getCopiedMessage(wrap) {
    var msg = wrap && wrap.getAttribute('data-url-copied-message');
    return (msg && msg.trim()) ? msg.trim() : 'URL is copied';
  }

  function copyText(text) {
    if (navigator.clipboard && navigator.clipboard.writeText) {
    return navigator.clipboard.writeText(text).then(function () { return true; }).catch(function () {
      return Promise.resolve(fallbackCopy(text));
    });
    }
    return Promise.resolve(fallbackCopy(text));
  }

  function fallbackCopy(text) {
    var ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.left = '-9999px';
    document.body.appendChild(ta);
    ta.select();
    try {
      return document.execCommand('copy');
    } catch (e) {
      return false;
    } finally {
      document.body.removeChild(ta);
    }
  }

  function showCopyFeedback(wrap) {
    var el = wrap.querySelector('.product-meta__copy-url-feedback');
    if (!el) return;
    el.textContent = getCopiedMessage(wrap);
    el.setAttribute('aria-hidden', 'false');
    el.classList.add('is-visible');
    if (hideTimeoutId) clearTimeout(hideTimeoutId);
    hideTimeoutId = setTimeout(function () {
      el.classList.remove('is-visible');
      el.setAttribute('aria-hidden', 'true');
      el.textContent = '';
      hideTimeoutId = null;
    }, 1800);
  }

  document.addEventListener('click', function (event) {
    var btn = event.target.closest('[data-copy-product-url]');
    if (!btn) return;
    event.preventDefault();
    var wrap = btn.closest('.product-meta__copy-url');
    if (!wrap) return;
    copyText(window.location.href).then(function (ok) {
      if (ok) showCopyFeedback(wrap);
    });
  });
})();

(function () {
  function markBandReady(wrapper) {
    if (!wrapper || wrapper.classList.contains('is-product-media-ready')) return;
    wrapper.classList.add('is-product-media-ready');
  }

  function getActiveProductList(wrapper) {
    return wrapper.querySelector('product-list:not([hidden])') || wrapper.querySelector('product-list');
  }

  function initFeaturedCollectionsBand(wrapper) {
    if (!wrapper || wrapper.getAttribute('data-band-init') === 'true') return;

    var band = wrapper.querySelector('.janvi-bg-band');
    if (!band) return;

    wrapper.setAttribute('data-band-init', 'true');

    var activeList = getActiveProductList(wrapper);
    if (!activeList) {
      markBandReady(wrapper);
      return;
    }

    var image = activeList.querySelector('.product-item__primary-image');
    if (!image) {
      markBandReady(wrapper);
      return;
    }

    if (image.complete && image.naturalWidth > 0) {
      markBandReady(wrapper);
      return;
    }

    var onMediaResolved = function () {
      markBandReady(wrapper);
      image.removeEventListener('load', onMediaResolved);
      image.removeEventListener('error', onMediaResolved);
    };

    image.addEventListener('load', onMediaResolved, { once: true });
    image.addEventListener('error', onMediaResolved, { once: true });

    // Fallback in case image events do not fire (cache/timing edge cases).
    setTimeout(onMediaResolved, 1800);
  }

  function initAllFeaturedCollectionsBands(root) {
    var context = root || document;
    var wrappers = context.querySelectorAll('.featured-collections');
    wrappers.forEach(initFeaturedCollectionsBand);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAllFeaturedCollectionsBands(document);
    });
  } else {
    initAllFeaturedCollectionsBands(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    initAllFeaturedCollectionsBands(event.target);
  });
})();

/* Judge.me cards-carousel — route prev/next button clicks to native scroll.
   component-review-card.css kills JM's `transform: translateX` (line 168) so
   we can use flex `gap` + native drag on .jdgm-cards-wrapper. Side effect: JM's
   inline `onclick="jdgmNextCard(...)"` becomes a no-op. Capture-phase listener
   intercepts before the inline handler runs and scrolls the wrapper by one card. */
(function () {
  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('.jdgm-arrow');
    if (!btn) return;

    e.stopImmediatePropagation();
    e.preventDefault();

    var widget = btn.closest('.jdgm-cards-carousel');
    if (!widget) return;
    var wrapper = widget.querySelector('.jdgm-cards-wrapper');
    var card = widget.querySelector('.jdgm-carousel-item');
    var track = widget.querySelector('.jdgm-videos-container');
    if (!wrapper || !card || !track) return;

    var cardW = card.getBoundingClientRect().width;
    var gap = parseFloat(getComputedStyle(track).columnGap) || 0;
    if (!gap && getComputedStyle(track).gap) {
      var parts = String(getComputedStyle(track).gap).trim().split(/\s+/);
      gap = parseFloat(parts[1] || parts[0]) || 0;
    }
    var isPrev = (btn.getAttribute('onclick') || '').indexOf('jdgmPrevious') !== -1;

    wrapper.scrollBy({
      left: isPrev ? -(cardW + gap) : (cardW + gap),
      behavior: 'smooth'
    });
  }, true);
})();

/* Quick View — swap similar product without leaving the drawer/popover */
(function () {
  document.addEventListener('click', function (event) {
    var link = event.target.closest('.similar-inline__thumb');
    if (!link) return;

    var quickBuy = link.closest('quick-buy-drawer, quick-buy-popover');
    if (!quickBuy || !quickBuy.hasAttribute('open')) return;

    var url = link.getAttribute('href');
    if (!url || url === quickBuy.getAttribute('href')) return;

    event.preventDefault();
    event.stopPropagation();

    quickBuy.setAttribute('href', url);

    if (typeof quickBuy._load === 'function') {
      quickBuy._load().then(function () {
        var scrollHost = quickBuy.querySelector('.drawer__content, .popover__content');
        if (scrollHost) scrollHost.scrollTop = 0;
      });
    }
  }, true);
})();


/* Judge.me cards-carousel — block JM's touch handlers so native swipe works.
   JM attaches non-passive touchmove listeners that preventDefault to drive its
   own translateX scroll. With our `transform: none` killing JM's visual scroll,
   those preventDefault calls also block the wrapper's native overflow-x scroll —
   producing flaky "sometimes scrolls, sometimes not" touch behavior.

   Document-level capture-phase listener runs BEFORE any descendant listener;
   stopImmediatePropagation halts the event chain so JM's listeners never fire.
   `passive: true` guarantees this listener can't preventDefault, so the browser's
   native scroll handler still receives the touch and pans the wrapper. */
(function () {
  ['touchstart', 'touchmove', 'touchend', 'touchcancel'].forEach(function (type) {
    document.addEventListener(type, function (e) {
      if (e.target.closest && e.target.closest('.jdgm-cards-wrapper')) {
        e.stopImmediatePropagation();
      }
    }, { capture: true, passive: true });
  });
})();